import React from 'react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { HALE_TEXT_FAINT, HALE_TEXT_MUTED, HALE_HAIR, HALE_INK, HALE_RED } from './_design';

/* Shared field primitives for the HALE intake. Cream/ivory editorial
 * surface — outline-only inputs, generous spacing, serif headings — so
 * the intake matches the rest of the Hale platform visually. */

export function StepHeader({ kicker, title, sub }) {
  return (
    <div className="mb-10">
      <p className="text-[10px] font-bold uppercase tracking-[0.32em]" style={{ color: HALE_RED }}>{kicker}</p>
      <h2 className="text-3xl md:text-4xl font-light mt-3 leading-tight tracking-tight"
          style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif', color: HALE_INK }}>
        {title}
      </h2>
      {sub && <p className="text-sm mt-3 max-w-xl leading-relaxed" style={{ color: HALE_TEXT_MUTED }}>{sub}</p>}
    </div>
  );
}

export function Field({ label, hint, optional, children, full }) {
  return (
    <label className={`block ${full ? 'col-span-2' : ''}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: HALE_INK }}>{label}</span>
        {optional && <span className="text-[9px] uppercase tracking-[0.18em]"
                            style={{ color: HALE_TEXT_FAINT }}>optional</span>}
      </div>
      {children}
      {hint && <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: HALE_TEXT_FAINT }}>{hint}</p>}
    </label>
  );
}

export function HaleInput(props) {
  return (
    <Input
      {...props}
      className={
        'bg-transparent border-0 border-b rounded-none px-0 h-11 text-base ' +
        'placeholder:text-[rgba(11,20,38,0.30)] focus-visible:ring-0 focus-visible:border-[#E63946] ' +
        (props.className || '')
      }
      style={{ borderBottomColor: HALE_HAIR, color: HALE_INK, ...(props.style || {}) }}
    />
  );
}

export function HaleTextarea(props) {
  return (
    <Textarea
      {...props}
      className={
        'bg-transparent border rounded-md text-base placeholder:text-[rgba(11,20,38,0.30)] ' +
        'focus-visible:ring-0 focus-visible:border-[#E63946] ' +
        (props.className || '')
      }
      style={{ borderColor: HALE_HAIR, color: HALE_INK, ...(props.style || {}) }}
    />
  );
}

export function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={
        'px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] rounded-full ' +
        'border transition-all ' +
        (active
          ? 'bg-[#E63946] text-black border-[#E63946]'
          : 'bg-transparent border-[rgba(11,20,38,0.18)] hover:border-[#0B1426] text-[#0B1426]/70 hover:text-[#0B1426]')
      }
    >
      {children}
    </button>
  );
}

export function ChipGrid({ options, value = [], onToggle, testidPrefix }) {
  const arr = Array.isArray(value) ? value : [];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const k = typeof opt === 'string' ? opt : opt.key;
        const label = typeof opt === 'string' ? opt : opt.label;
        return (
          <Chip key={k} active={arr.includes(k)} onClick={() => onToggle(k)}
                testid={testidPrefix ? `${testidPrefix}-${k}` : undefined}>
            {label}
          </Chip>
        );
      })}
    </div>
  );
}

export function Stepper({ current, total }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => {
        const idx = i + 1;
        const isDone = idx < current;
        const isOn = idx === current;
        return (
          <div key={idx} className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold tabular-nums transition-all"
              style={{
                backgroundColor: isOn ? '#E63946' : (isDone ? 'rgba(230,57,70,0.18)' : 'transparent'),
                color: isOn ? '#FFFFFF' : (isDone ? '#E63946' : HALE_TEXT_FAINT),
                border: isOn ? '1px solid #E63946' : `1px solid ${HALE_HAIR}`,
              }}
            >
              {idx}
            </div>
            {idx < total && <div className="w-8 h-px" style={{ backgroundColor: HALE_HAIR }} />}
          </div>
        );
      })}
    </div>
  );
}
